import { AITool } from '../types';

// Comprehensive database of AI tools organized by category and use case
export const aiTools: AITool[] = [
  // MARKETING CATEGORY
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    description: 'Versatile AI assistant for text generation and conversation',
    logo: 'https://images.unsplash.com/photo-1679083216051-aa510a1a2c0e?q=80&w=100&auto=format&fit=crop',
    website: 'https://chat.openai.com',
    compatibilityScore: 95,
    categories: ['marketing', 'productivity', 'creative'],
    useCases: ['writing', 'research', 'coding'],
    experienceLevels: ['beginner', 'intermediate', 'advanced'],
    overview: 'ChatGPT is an AI language model developed by OpenAI that can engage in conversational dialogue and provide responses that can feel surprisingly human. It can draft content, answer questions, assist with creative writing, generate code, and help with a wide range of text-based tasks.',
    gettingStarted: '1. Visit chat.openai.com\n2. Sign up for an account (free tier available)\n3. Start a new chat\n4. Type your question or request\n5. Refine your prompts for better results',
    tryNowLink: 'https://chat.openai.com',
    pricingType: 'freemium'
  },
  {
    id: 'jasper',
    name: 'Jasper',
    description: 'AI content platform for marketing teams',
    logo: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?q=80&w=100&auto=format&fit=crop',
    website: 'https://www.jasper.ai',
    compatibilityScore: 92,
    categories: ['marketing'],
    useCases: ['writing', 'images'],
    experienceLevels: ['beginner', 'intermediate'],
    overview: 'Jasper is an AI content creation platform designed specifically for marketing teams. It can generate blog posts, social media content, email copy, ad text, and more. It includes templates for different content types and integrates with tools like Grammarly and Copyscape.',
    gettingStarted: '1. Sign up for a Jasper account\n2. Choose a content template\n3. Enter your topic and keywords\n4. Generate content with AI\n5. Edit and refine the output',
    tryNowLink: 'https://www.jasper.ai',
    pricingType: 'paid'
  },
  {
    id: 'copy-ai',
    name: 'Copy.ai',
    description: 'AI copywriting tool for marketing content',
    logo: 'https://images.unsplash.com/photo-1560472355-536de3962603?q=80&w=100&auto=format&fit=crop',
    website: 'https://www.copy.ai',
    compatibilityScore: 88,
    categories: ['marketing'],
    useCases: ['writing'],
    experienceLevels: ['beginner', 'intermediate'],
    overview: 'Copy.ai is an AI-powered copywriting tool that helps marketers create high-converting copy for various channels. It offers templates for social media posts, ads, product descriptions, emails, and more, making it easy to generate engaging content quickly.',
    gettingStarted: '1. Create a Copy.ai account\n2. Select a content type template\n3. Enter your product/brand information\n4. Generate multiple copy variations\n5. Edit and use the content in your campaigns',
    tryNowLink: 'https://www.copy.ai',
    pricingType: 'freemium'
  },
  
  // HEALTHCARE CATEGORY
  {
    id: 'mediscan-ai',
    name: 'MediScan AI',
    description: 'Advanced medical imaging analysis for healthcare professionals',
    logo: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=100&auto=format&fit=crop',
    website: 'https://example.com/mediscan',
    compatibilityScore: 90,
    categories: ['healthcare'],
    useCases: ['images', 'data-analysis'],
    experienceLevels: ['intermediate', 'advanced'],
    overview: 'MediScan AI uses advanced machine learning algorithms to analyze medical images, helping healthcare professionals detect abnormalities and make more accurate diagnoses. The platform supports various imaging modalities including X-rays, MRIs, and CT scans.',
    gettingStarted: '1. Request a demo through our website\n2. Work with our team to integrate with your existing systems\n3. Upload medical images for analysis\n4. Review AI-generated insights and annotations\n5. Export reports for patient records',
    tryNowLink: 'https://example.com/mediscan/try',
    pricingType: 'paid'
  },
  {
    id: 'healthgpt',
    name: 'HealthGPT',
    description: 'AI assistant for medical information and patient education',
    logo: 'https://images.unsplash.com/photo-1551076805-e1869033e561?q=80&w=100&auto=format&fit=crop',
    website: 'https://example.com/healthgpt',
    compatibilityScore: 85,
    categories: ['healthcare'],
    useCases: ['writing', 'research'],
    experienceLevels: ['beginner', 'intermediate'],
    overview: 'HealthGPT is an AI assistant designed specifically for healthcare professionals and patients. It can provide evidence-based medical information, help with patient education materials, and assist with medical research by summarizing scientific literature.',
    gettingStarted: '1. Sign up for an account\n2. Choose your role (healthcare provider or patient)\n3. Ask medical questions or request information\n4. Receive evidence-based responses\n5. Save and share information as needed',
    tryNowLink: 'https://example.com/healthgpt/try',
    pricingType: 'freemium'
  },
  {
    id: 'medicalai',
    name: 'MedicalAI',
    description: 'AI-powered clinical decision support system',
    logo: 'https://images.unsplash.com/photo-1581093458791-9d2b0e2f9212?q=80&w=100&auto=format&fit=crop',
    website: 'https://example.com/medicalai',
    compatibilityScore: 88,
    categories: ['healthcare'],
    useCases: ['data-analysis', 'research'],
    experienceLevels: ['intermediate', 'advanced'],
    overview: 'MedicalAI is a clinical decision support system that helps healthcare providers make more informed treatment decisions. It analyzes patient data, medical history, and the latest research to provide personalized treatment recommendations and risk assessments.',
    gettingStarted: '1. Contact our sales team for implementation\n2. Integrate with your electronic health record system\n3. Train your staff on using the platform\n4. Start receiving AI-powered clinical insights\n5. Monitor outcomes and adjust as needed',
    tryNowLink: 'https://example.com/medicalai/try',
    pricingType: 'paid'
  },
  
  // FINANCE CATEGORY
  {
    id: 'finpredict',
    name: 'FinPredict',
    description: 'AI-powered financial forecasting and investment analysis',
    logo: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=100&auto=format&fit=crop',
    website: 'https://example.com/finpredict',
    compatibilityScore: 85,
    categories: ['finance'],
    useCases: ['data-analysis', 'automation'],
    experienceLevels: ['intermediate', 'advanced'],
    overview: 'FinPredict uses machine learning to analyze market trends, company financials, and economic indicators to provide investment recommendations and financial forecasts. The platform helps financial professionals make data-driven decisions and optimize investment strategies.',
    gettingStarted: '1. Sign up for a subscription plan\n2. Connect your data sources or import financial datasets\n3. Set up your analysis parameters\n4. Review AI-generated forecasts and recommendations\n5. Export reports or integrate with your trading platforms',
    tryNowLink: 'https://example.com/finpredict/try',
    pricingType: 'paid'
  },
  {
    id: 'wealthai',
    name: 'WealthAI',
    description: 'Personal finance management with AI insights',
    logo: 'https://images.unsplash.com/photo-1565514020179-026b92b2d70b?q=80&w=100&auto=format&fit=crop',
    website: 'https://example.com/wealthai',
    compatibilityScore: 90,
    categories: ['finance'],
    useCases: ['automation', 'data-analysis'],
    experienceLevels: ['beginner', 'intermediate'],
    overview: 'WealthAI is a personal finance management tool that uses AI to provide personalized financial advice, budget optimization, and investment recommendations. It connects to your bank accounts and credit cards to analyze spending patterns and help you achieve your financial goals.',
    gettingStarted: '1. Create an account and connect your financial accounts\n2. Set your financial goals and preferences\n3. Review your AI-generated financial analysis\n4. Implement recommended actions\n5. Track your progress over time',
    tryNowLink: 'https://example.com/wealthai/try',
    pricingType: 'freemium'
  },
  {
    id: 'fraudshield',
    name: 'FraudShield',
    description: 'AI-powered fraud detection for financial institutions',
    logo: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=100&auto=format&fit=crop',
    website: 'https://example.com/fraudshield',
    compatibilityScore: 92,
    categories: ['finance'],
    useCases: ['data-analysis', 'automation'],
    experienceLevels: ['intermediate', 'advanced'],
    overview: 'FraudShield uses advanced AI algorithms to detect and prevent fraudulent transactions in real-time. It analyzes transaction patterns, user behavior, and other risk factors to identify potential fraud before it happens, helping financial institutions reduce losses and protect customers.',
    gettingStarted: '1. Schedule a consultation with our security experts\n2. Integrate our API with your payment systems\n3. Configure risk thresholds and alert preferences\n4. Monitor the fraud detection dashboard\n5. Regularly review and update security rules',
    tryNowLink: 'https://example.com/fraudshield/try',
    pricingType: 'paid'
  },
  
  // EDUCATION CATEGORY
  {
    id: 'eduai-tutor',
    name: 'EduAI Tutor',
    description: 'Personalized learning assistant for students of all ages',
    logo: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=100&auto=format&fit=crop',
    website: 'https://example.com/eduai',
    compatibilityScore: 92,
    categories: ['education'],
    useCases: ['writing', 'research'],
    experienceLevels: ['beginner', 'intermediate'],
    overview: 'EduAI Tutor is a personalized learning assistant that adapts to each student\'s learning style and pace. The platform provides interactive lessons, practice exercises, and instant feedback across various subjects, helping students improve their understanding and academic performance.',
    gettingStarted: '1. Create a student profile\n2. Take the learning style assessment\n3. Select subjects and topics of interest\n4. Start interactive lessons and exercises\n5. Track progress through the dashboard',
    tryNowLink: 'https://example.com/eduai/try',
    pricingType: 'freemium'
  },
  {
    id: 'quizgenius',
    name: 'QuizGenius',
    description: 'AI-powered quiz and assessment creation tool',
    logo: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?q=80&w=100&auto=format&fit=crop',
    website: 'https://example.com/quizgenius',
    compatibilityScore: 88,
    categories: ['education'],
    useCases: ['writing', 'automation'],
    experienceLevels: ['beginner', 'intermediate'],
    overview: 'QuizGenius helps educators create high-quality quizzes and assessments in minutes. The AI analyzes learning materials and automatically generates relevant questions with varying difficulty levels, saving teachers time while ensuring comprehensive coverage of the subject matter.',
    gettingStarted: '1. Sign up for an educator account\n2. Upload your learning materials or syllabus\n3. Specify the quiz parameters (length, difficulty, etc.)\n4. Generate and review the questions\n5. Share with students or export to your LMS',
    tryNowLink: 'https://example.com/quizgenius/try',
    pricingType: 'freemium'
  },
  {
    id: 'studyai',
    name: 'StudyAI',
    description: 'AI study assistant for exam preparation',
    logo: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?q=80&w=100&auto=format&fit=crop',
    website: 'https://example.com/studyai',
    compatibilityScore: 90,
    categories: ['education'],
    useCases: ['research', 'writing'],
    experienceLevels: ['beginner', 'intermediate', 'advanced'],
    overview: 'StudyAI is an intelligent study companion that helps students prepare for exams more effectively. It creates personalized study plans, generates practice questions, provides explanations for complex topics, and uses spaced repetition to optimize learning and retention.',
    gettingStarted: '1. Create an account and select your courses\n2. Upload your study materials or notes\n3. Set your exam date and study goals\n4. Follow the AI-generated study plan\n5. Take practice tests and review weak areas',
    tryNowLink: 'https://example.com/studyai/try',
    pricingType: 'freemium'
  },
  
  // PRODUCTIVITY CATEGORY
  {
    id: 'taskmaster-ai',
    name: 'TaskMaster AI',
    description: 'Intelligent task management and productivity optimization',
    logo: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?q=80&w=100&auto=format&fit=crop',
    website: 'https://example.com/taskmaster',
    compatibilityScore: 88,
    categories: ['productivity'],
    useCases: ['automation', 'writing'],
    experienceLevels: ['beginner', 'intermediate', 'advanced'],
    overview: 'TaskMaster AI is a smart productivity tool that helps you manage tasks, optimize your schedule, and automate routine work. The platform uses AI to prioritize tasks, suggest optimal work times, and even draft emails and messages based on your communication style.',
    gettingStarted: '1. Sign up and connect your calendar and email\n2. Import existing tasks or create new ones\n3. Let the AI analyze your work patterns\n4. Review AI-suggested schedule optimizations\n5. Enable automation features as needed',
    tryNowLink: 'https://example.com/taskmaster/try',
    pricingType: 'freemium'
  },
  {
    id: 'notion-ai',
    name: 'Notion AI',
    description: 'AI writing assistant integrated with Notion workspace',
    logo: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=100&auto=format&fit=crop',
    website: 'https://www.notion.so/product/ai',
    compatibilityScore: 88,
    categories: ['productivity', 'marketing'],
    useCases: ['writing', 'data-analysis', 'research'],
    experienceLevels: ['beginner', 'intermediate'],
    overview: 'Notion AI is an AI writing assistant built directly into the Notion workspace. It can help draft content, summarize text, generate ideas, translate languages, and improve writing. It\'s particularly useful for teams that already use Notion for documentation and project management.',
    gettingStarted: '1. Sign up for Notion\n2. Enable Notion AI in your workspace\n3. Use the AI menu or slash command (/ai)\n4. Choose a function like "summarize," "draft," or "improve"\n5. Edit and refine the AI-generated content',
    tryNowLink: 'https://www.notion.so/product/ai',
    pricingType: 'paid'
  },
  {
    id: 'otter-ai',
    name: 'Otter.ai',
    description: 'AI meeting assistant for transcription and summaries',
    logo: 'https://images.unsplash.com/photo-1558655146-d09347e92766?q=80&w=100&auto=format&fit=crop',
    website: 'https://otter.ai',
    compatibilityScore: 87,
    categories: ['productivity'],
    useCases: ['audio', 'automation'],
    experienceLevels: ['beginner', 'intermediate'],
    overview: 'Otter.ai is an AI-powered meeting assistant that provides real-time transcription, automated meeting notes, and conversation summaries. It integrates with video conferencing platforms like Zoom and Google Meet to automatically record and transcribe meetings.',
    gettingStarted: '1. Create an Otter.ai account\n2. Connect to your calendar\n3. Join meetings with Otter\n4. Get automatic transcriptions\n5. Review and share meeting notes',
    tryNowLink: 'https://otter.ai',
    pricingType: 'freemium'
  },
  
  // CREATIVE CATEGORY
  {
    id: 'midjourney',
    name: 'Midjourney',
    description: 'AI image generation with stunning artistic quality',
    logo: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=100&auto=format&fit=crop',
    website: 'https://www.midjourney.com',
    compatibilityScore: 90,
    categories: ['creative'],
    useCases: ['images'],
    experienceLevels: ['beginner', 'intermediate', 'advanced'],
    overview: 'Midjourney is an AI art generator that creates images from textual descriptions. It\'s known for its artistic style and high-quality outputs, making it popular among designers, artists, and creative professionals who need visual content.',
    gettingStarted: '1. Join the Midjourney Discord server\n2. Navigate to a newbie channel\n3. Use the /imagine command followed by your prompt\n4. Select variations or upscale your favorite results\n5. Download your images',
    tryNowLink: 'https://www.midjourney.com',
    pricingType: 'paid'
  },
  {
    id: 'designdream',
    name: 'DesignDream',
    description: 'AI-powered design assistant for creative professionals',
    logo: 'https://images.unsplash.com/photo-1558655146-d09347e92766?q=80&w=100&auto=format&fit=crop',
    website: 'https://example.com/designdream',
    compatibilityScore: 94,
    categories: ['creative'],
    useCases: ['images', 'video'],
    experienceLevels: ['intermediate', 'advanced'],
    overview: 'DesignDream is an AI design assistant that helps creative professionals generate and refine visual content. From concept sketches to finished designs, the platform offers tools for image generation, style transfer, color palette suggestions, and layout optimization.',
    gettingStarted: '1. Create an account and set up your design preferences\n2. Start a new project and describe your vision\n3. Generate initial concepts with AI\n4. Refine and iterate on designs\n5. Export in various formats for different platforms',
    tryNowLink: 'https://example.com/designdream/try',
    pricingType: 'paid'
  },
  {
    id: 'stable-diffusion',
    name: 'Stable Diffusion',
    description: 'Open-source AI image generation model',
    logo: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=100&auto=format&fit=crop',
    website: 'https://stability.ai',
    compatibilityScore: 85,
    categories: ['creative'],
    useCases: ['images'],
    experienceLevels: ['intermediate', 'advanced'],
    overview: 'Stable Diffusion is an open-source AI model that generates detailed images from text descriptions. Unlike some alternatives, it can be run locally on your own hardware and customized for specific use cases. It\'s popular among developers and technical users who want more control over their AI image generation.',
    gettingStarted: '1. Use a hosted version like DreamStudio or\n2. Set up locally (requires technical knowledge)\n3. Write detailed prompts for best results\n4. Experiment with different settings\n5. Join the community to learn advanced techniques',
    tryNowLink: 'https://stability.ai',
    pricingType: 'freemium'
  }
];